import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, Info, Search } from "lucide-react";
import { collapseTimeline, formatDuration, matchesQuery } from "../executionTimeline";
import TimelineRow, { COLS } from "./TimelineRow";
import EntryDetailPanel from "./EntryDetailPanel";

// La pestaña de timeline: todo lo que pasó, en el orden en que pasó, sin
// agrupar. Es la lectura opuesta a la del resumen — ahí se busca la forma de
// la conversación, acá el segundo exacto en que algo salió mal.

const Header = () => (
  <div className="flex items-center gap-3 border-b border-white/10 bg-black/20 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-gray-600">
    <span className={`${COLS.seq} shrink-0`}>seq</span>
    <span className={`${COLS.kind} shrink-0`}>kind</span>
    <span className={COLS.name}>name</span>
    <span className={`${COLS.detail} shrink-0`}>detail</span>
    <span className={`${COLS.duration} shrink-0 text-right`}>duration</span>
    <span className={`${COLS.status} shrink-0`}>status</span>
    <span className={`${COLS.cost} shrink-0 text-right`}>cost</span>
    <span className={COLS.chevron} />
  </div>
);

/**
 * Las corridas seguidas que no tienen nada que contar, plegadas. La fila dice
 * cuántas son y cuánto sumaron, así que esconde el detalle pero no el hecho.
 */
const RangeRow = ({ row, onExpand }) => (
  <button
    type="button"
    onClick={onExpand}
    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-white/5"
  >
    <span className={`${COLS.seq} shrink-0 font-mono text-[10px] text-gray-700`}>
      {row.from}–{row.to}
    </span>
    <span className={`${COLS.kind} shrink-0`} />
    <span className={`${COLS.name} truncate text-xs text-gray-500`}>
      {row.entries.length} step runs with nothing to report
    </span>
    <span className={`${COLS.detail} shrink-0 text-[10px] text-gray-600`}>show them</span>
    <span className={`${COLS.duration} shrink-0 text-right text-[11px] text-gray-500`}>
      {formatDuration(row.durationMs)}
    </span>
    <span className={`${COLS.status} shrink-0`} />
    <span className={`${COLS.cost} shrink-0`} />
    <ChevronDown className={`${COLS.chevron} h-3.5 shrink-0 text-gray-700`} />
  </button>
);

export default function ExecutionTimelineView({
  runId,
  projectId,
  timeline,
  degraded,
  degradedReason,
  truncated,
  truncatedReason,
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [expandedRanges, setExpandedRanges] = useState(() => new Set());

  const entries = useMemo(() => timeline || [], [timeline]);

  // Buscando no se pliega nada: quien busca quiere ver todo lo que coincide.
  const rows = useMemo(() => {
    if (query.trim()) {
      return entries.filter((entry) => matchesQuery(entry, query)).map((entry) => ({ type: "entry", entry }));
    }
    return collapseTimeline(entries);
  }, [entries, query]);

  const expand = (key) =>
    setExpandedRanges((current) => new Set(current).add(key));

  const shownCount = rows.reduce(
    (total, row) => total + (row.type === "range" ? row.entries.length : 1),
    0
  );

  return (
    <div className="flex h-full min-h-0 gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {degraded && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-[11px] text-amber-200/90">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <span className="font-medium">Partial history.</span> One entry per step instead of
              one per run{degradedReason ? ` (${degradedReason})` : ""}, without inputs, and each
              step carries the timestamps of its last pass.
            </div>
          </div>
        )}

        {truncated && (
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-500/30 bg-blue-500/5 px-3 py-2.5 text-[11px] text-blue-200/90">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <span className="font-medium">Showing part of the run.</span>{" "}
              {truncatedReason || "This execution has more runs than one page returns."}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or type…"
              className="w-full rounded-lg border border-white/10 bg-black/30 py-1.5 pl-8 pr-3 text-xs text-gray-200 placeholder:text-gray-600 focus:border-violet-500/40 focus:outline-none"
            />
          </div>
          <span className="text-[10px] text-gray-500">
            {shownCount} of {entries.length} entries
          </span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-zinc-900/40">
          <Header />
          <div className="min-h-0 flex-1 divide-y divide-white/5 overflow-auto">
            {rows.length === 0 && (
              <div className="flex h-32 items-center justify-center text-xs text-gray-500">
                Nothing matches “{query}”.
              </div>
            )}
            {rows.map((row) => {
              if (row.type === "entry") {
                return (
                  <TimelineRow
                    key={row.entry.seq}
                    entry={row.entry}
                    selected={selected?.seq === row.entry.seq}
                    onSelect={setSelected}
                  />
                );
              }
              const key = `${row.from}-${row.to}`;
              if (!expandedRanges.has(key)) {
                return <RangeRow key={key} row={row} onExpand={() => expand(key)} />;
              }
              return row.entries.map((entry) => (
                <TimelineRow
                  key={entry.seq}
                  entry={entry}
                  selected={selected?.seq === entry.seq}
                  onSelect={setSelected}
                />
              ));
            })}
          </div>
        </div>
      </div>

      <div className="w-80 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-zinc-900/50">
        <EntryDetailPanel
          runId={runId}
          projectId={projectId}
          entry={selected}
          onClose={() => setSelected(null)}
        />
      </div>
    </div>
  );
}
